import { memo, useCallback, useEffect, useRef, useState } from "react"
import Input from ".."
import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"

export type DaterangeValue = {
  from?: string
  to?: string
}

type Daterange = {
  value: DaterangeValue
  onChange: (val: string) => void
  size?: string
  hideLabel?: boolean
  clamping?: DaterangeValue[]
}

const Daterange = memo(({
  value, onChange, size = 'l', hideLabel = true, clamping = []
}: Daterange) => {
  const { t } = useTranslation()
  const fromRef = useRef()
  const toRef = useRef()

  const [fromDate, setFromDate] = useState<string>(DateTime.now().toISO())
  const [toDate, setToDate] = useState<string>(DateTime.now().toISO())

  const fixTime = useCallback((dates: DaterangeValue) => {
    const { from, to } = dates
    const luxonFrom = DateTime.fromISO(from).set({ hour: 0, minute: 0, second: 0 })
    const luxonTo = DateTime.fromISO(to).set({ hour: 23, minute: 59, second: 59 })
    return {
      from: luxonFrom.toUTC().toISO(),
      to: luxonTo.toUTC().toISO()
    }
  }, [])

  useEffect(() => {
    const newTime = fixTime({ from: fromDate, to: toDate })
    onChange(newTime)
  }, [fromDate, toDate])

  const handleFromChange = useCallback((val: string) => {
    setFromDate(val)
    const newFrom = DateTime.fromISO(val).startOf('day')
    const currentTo = DateTime.fromISO(toDate).startOf('day')
    if (newFrom.isValid && currentTo.isValid && newFrom > currentTo) {
      setToDate(val)
    }
  }, [toDate])

  return (
    <div className="Daterange">
      <Input
        ref={fromRef}
        type="date"
        hideLabel={hideLabel}
        label={t('From')}
        dateKey="from"
        size={size}
        value={value}
        onInput={handleFromChange}
        onSelect={() => (toRef.current as any)?.open?.()}
      />
      <Input
        ref={toRef}
        type="date"
        hideLabel={hideLabel}
        label={t('To')}
        dateKey="to"
        size={size}
        value={value}
        onInput={(val) => setToDate(val)}
        config={{ clamping: [...clamping, { to: value?.from ? DateTime.fromISO(value.from).minus({ days: 1 }).toISODate() ?? undefined : undefined }] }}
      />
    </div>
  )
})

export default Daterange
